import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { XMarkIcon, ArrowPathIcon, FolderOpenIcon, BoltIcon } from '@heroicons/react/24/outline';
import type { Agrupamento } from '../../api/agrupamento';
import { GridProdutosGrupo, GridProdutosDisponiveis } from './GridProdutos';
import { useGrupoProdutos } from '../../hooks/useGrupoProdutos';

type Aba = 'grupo' | 'adicionar';

function extrairTermoComum(descricoes: string[]): string {
  const validas = descricoes.filter(d => d.trim().length > 0);
  if (validas.length === 0) return '';
  const listas = validas.map(d =>
    d.toUpperCase().split(/[\s,./\\-]+/).filter(w => w.length > 2),
  );
  const referencia = listas[0];
  const comuns = referencia.filter(palavra =>
    listas.every(lista => lista.includes(palavra)),
  );
  return comuns.slice(0, 2).join(' ');
}

interface Props {
  agrupamento: Agrupamento;
  termoSugeridoApi: string;
  qtdeSugeridos: number;
  abaInicial?: Aba;
  onFechar: () => void;
  onAgrupamentosChange: (produtosAdicionados?: number) => void;
}

export const PainelGrupoSelecionado: React.FC<Props> = ({
  agrupamento,
  termoSugeridoApi,
  qtdeSugeridos,
  abaInicial = 'grupo',
  onFechar,
  onAgrupamentosChange,
}) => {
  const [aba, setAba] = useState<Aba>(abaInicial);
  const [adicionandoTodos, setAdicionandoTodos] = useState(false);

  const {
    produtosGrupo,
    produtosDisponiveis,
    carregandoGrupo,
    carregandoDisponiveis,
    carregarProdutosGrupo,
    buscarDisponiveis,
    vincularProdutos,
    removerProduto,
    limpar,
  } = useGrupoProdutos();

  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    limpar();
    setAba(abaInicial);
    carregarProdutosGrupo(agrupamento.grupoId);
  }, [agrupamento.grupoId, carregarProdutosGrupo, limpar, abaInicial]);

  const termoSugeridoLocal = useMemo(
    () => extrairTermoComum(produtosGrupo.map(p => p.descricaoProduto ?? '')),
    [produtosGrupo],
  );
  const termoSugerido = termoSugeridoApi || termoSugeridoLocal;

  const handleBuscarDisponiveis = useCallback(
    (termo: string) => buscarDisponiveis(agrupamento.grupoId, termo),
    [agrupamento.grupoId, buscarDisponiveis],
  );

  const handleVincular = useCallback(
    async (produtos: Array<{ codigo: string; embalagem: string; visivelCat: string }>) => {
      await vincularProdutos(agrupamento.grupoId, produtos);
      onAgrupamentosChange(produtos.length);
      setAba('grupo');
    },
    [agrupamento.grupoId, vincularProdutos, onAgrupamentosChange],
  );

  const handleAdicionarTodos = useCallback(async () => {
    const qtde = produtosDisponiveis.length;
    if (qtde === 0) return;
    setAdicionandoTodos(true);
    try {
      await vincularProdutos(
        agrupamento.grupoId,
        produtosDisponiveis.map(p => ({ codigo: p.codigo, embalagem: p.embalagem ?? '', visivelCat: 'S' })),
      );
      onAgrupamentosChange(qtde);
      setAba('grupo');
    } finally {
      setAdicionandoTodos(false);
    }
  }, [produtosDisponiveis, agrupamento.grupoId, vincularProdutos, onAgrupamentosChange]);

  const handleRemover = useCallback(
    async (codigo: string) => {
      await removerProduto(agrupamento.grupoId, codigo);
      onAgrupamentosChange();
    },
    [agrupamento.grupoId, removerProduto, onAgrupamentosChange],
  );

  const handleRecarregar = useCallback(
    () => carregarProdutosGrupo(agrupamento.grupoId, true),
    [agrupamento.grupoId, carregarProdutosGrupo],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Cabeçalho */}
      <div className="flex-none px-3 py-2 md:px-4 md:py-3 bg-white dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between gap-2 md:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpenIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-xs md:text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                {agrupamento.descricao}
              </h2>
              <p className="text-[10px] md:text-xs text-zinc-500 dark:text-zinc-400">
                {produtosGrupo.length} produto{produtosGrupo.length !== 1 ? 's' : ''} no grupo
                {qtdeSugeridos > 0 && aba !== 'adicionar' && (
                  <span className="ml-1 md:ml-2 text-amber-600 dark:text-amber-400 font-medium">
                    · {qtdeSugeridos} sugerido{qtdeSugeridos !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRecarregar}
              disabled={carregandoGrupo}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 disabled:opacity-50 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
              title="Recarregar"
            >
              <ArrowPathIcon className={`w-4 h-4 ${carregandoGrupo ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onFechar}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors hidden md:block"
              title="Fechar painel"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Abas */}
        <div className="flex items-center gap-1 mt-2 md:mt-3 flex-wrap">
          <button
            onClick={() => setAba('grupo')}
            className={`px-2 md:px-3 py-1.5 text-[11px] md:text-xs font-medium rounded-md transition-colors ${
              aba === 'grupo'
                ? 'bg-blue-600 text-white'
                : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }`}
          >
            Produtos do grupo
            {produtosGrupo.length > 0 && (
              <span className={`ml-1 md:ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                aba === 'grupo' ? 'bg-blue-500 text-white' : 'bg-zinc-200 dark:bg-zinc-600 text-zinc-600 dark:text-zinc-300'
              }`}>
                {produtosGrupo.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setAba('adicionar')}
            className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 text-[11px] md:text-xs font-medium rounded-md transition-colors ${
              aba === 'adicionar'
                ? 'bg-blue-600 text-white'
                : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }`}
          >
            + Adicionar
            {qtdeSugeridos > 0 && aba !== 'adicionar' && (
              <span className="px-1 md:px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 font-normal text-[10px]">
                {qtdeSugeridos}
              </span>
            )}
          </button>

          {/* Botão "Adicionar todos" */}
          {aba === 'adicionar' && produtosDisponiveis.length > 0 && (
            <button
              onClick={handleAdicionarTodos}
              disabled={adicionandoTodos || carregandoDisponiveis}
              className="ml-auto flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 text-[11px] md:text-xs font-medium rounded-md
                         bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors"
              title={`Adicionar todos os ${produtosDisponiveis.length} produtos ao grupo`}
            >
              <BoltIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">
                {adicionandoTodos ? 'Adicionando...' : `Adicionar todos (${produtosDisponiveis.length})`}
              </span>
              <span className="sm:hidden">
                {adicionandoTodos ? '...' : `Todos (${produtosDisponiveis.length})`}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-hidden p-2 md:p-4">
        {aba === 'grupo' ? (
          <GridProdutosGrupo
            grupoId={agrupamento.grupoId}
            produtos={produtosGrupo}
            carregando={carregandoGrupo}
            onRemover={handleRemover}
            onAlterado={onAgrupamentosChange}
          />
        ) : (
          <GridProdutosDisponiveis
            key={`disp-${agrupamento.grupoId}`}
            produtos={produtosDisponiveis}
            carregando={carregandoDisponiveis}
            onBuscar={handleBuscarDisponiveis}
            onVincular={handleVincular}
            initialTermo={termoSugerido}
          />
        )}
      </div>
    </div>
  );
};
